import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Store as StoreIcon } from "lucide-react";
import { prepareBrandImage } from '@/lib/prepare-brand-image';

const emptyIdentity = { address: '', phone: '', footer: '*** TERIMA KASIH ***', receipt_header_mode: 'text', receipt_header_image: null, receipt_header_width_mm: 35, receipt_name_size: 40 };

export default function Stores() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [identity, setIdentity] = useState(emptyIdentity);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const edit = async (store) => {
    setBusy(true); setError('');
    try {
      const { data } = await api.get(`/stores/${store.id}/identity`);
      setEditing(store); setName(store.name); setIdentity(data); setOpen(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true); setError('');
    try {
      const prepared = await prepareBrandImage(file);
      const form = new FormData(); form.append('file', prepared);
      const { data } = await api.post('/settings/receipt-header-image', form);
      setIdentity(previous => ({ ...previous, receipt_header_image: data.image, receipt_header_mode: 'image' }));
    } catch (e) { setError(e.response ? apiError(e) : e.message); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try { setItems((await api.get("/stores")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) return toast.error("Nama toko wajib diisi");
    setBusy(true); setError('');
    try {
      if (editing) await api.put(`/stores/${editing.id}/identity`, { ...identity, store_name: name });
      else await api.post("/stores", { name });
      toast.success(editing ? "Cabang disimpan" : "Cabang ditambahkan");
      setOpen(false); load();
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Profil Toko & Cabang" subtitle="Nama, logo struk, alamat, dan telepon tersimpan terpisah untuk setiap cabang. Logo aplikasi MASIVI tetap di Pengaturan Tema." />
      <div className="flex-1 overflow-y-auto p-5">
        <div className="flex justify-end mb-4"><WriteButton disabled={busy} onClick={() => { setEditing(null); setName(""); setIdentity(emptyIdentity); setError(''); setOpen(true); }} className="bg-brand-button hover:bg-brand-hover text-ui-text font-bold gap-2"><Plus className="h-4 w-4" /> Tambah Cabang</WriteButton></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`store-card-${s.id}`} className="rounded-xl border p-4 border-ui-line bg-ui-soft">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-brand-soft text-brand-readable flex items-center justify-center"><StoreIcon className="h-5 w-5" /></div>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <WriteButton plain disabled={busy} aria-label={`Identitas ${s.name}`} data-testid={`edit-store-${s.id}`} onClick={() => edit(s)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center"><Pencil className="h-4 w-4 text-ui-muted" /></WriteButton>
                </div>
              </div>
            </div>
          ))}
          {!items.length && <p className="text-sm text-ui-muted">Pilih tenant untuk melihat profil toko.</p>}
        </div>
      </div>
      <Dialog open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }}>
        <DialogContent data-testid="store-form-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Profil Cabang" : "Tambah Cabang"}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="branch-name" className="text-xs text-ui-muted">Nama Toko</Label>
            <Input id="branch-name" disabled={busy} data-testid="store-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-ui-card border-ui-line" placeholder="Nama toko" />
          </div>
          {editing && <fieldset disabled={busy} className="space-y-3">
            {['address', 'phone', 'footer'].map((field, index) => <div key={field} className="space-y-1">
              <Label htmlFor={`branch-${field}`}>{['Alamat', 'Telepon', 'Pesan bawah struk'][index]}</Label>
              <Input id={`branch-${field}`} value={identity[field] || ''} onChange={event => setIdentity(previous => ({ ...previous, [field]: event.target.value }))} />
            </div>)}
            <div className="space-y-1">
              <Label htmlFor="branch-header-mode">Judul struk</Label>
              <select id="branch-header-mode" className="w-full rounded-md border border-ui-line bg-ui-card p-2" value={identity.receipt_header_mode} onChange={event => setIdentity(previous => ({ ...previous, receipt_header_mode: event.target.value }))}>
                <option value="text">Nama toko</option><option value="image" disabled={!identity.receipt_header_image}>Logo cabang</option><option value="none">Tanpa judul</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="branch-logo">Unggah / ganti logo cabang</Label>
              <Input id="branch-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} />
              <p className="text-xs text-ui-muted">Gambar dikompres otomatis. Tekan Simpan untuk menerapkan hanya ke {editing.name}.</p>
              {identity.receipt_header_image && <img src={identity.receipt_header_image} alt={`Pratinjau logo ${name}`} className="max-h-24 max-w-full object-contain bg-white p-2 rounded" />}
            </div>
            <div className="space-y-1">
              <Label htmlFor="branch-logo-width">Lebar logo: {identity.receipt_header_width_mm} mm</Label>
              <input id="branch-logo-width" className="w-full" type="range" min="25" max="40" value={identity.receipt_header_width_mm} onChange={event => setIdentity(previous => ({ ...previous, receipt_header_width_mm: Number(event.target.value) }))} />
            </div>
          </fieldset>}
          {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
          <DialogFooter>
            <Button disabled={busy} variant="outline" onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton disabled={busy} data-testid="save-store-btn" onClick={save} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">{busy ? 'Memproses…' : 'Simpan'}</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
